import type { Metadata } from "next";

export const SITE_NAME = "Brains for Robots";
export const COMPANY_NAME = "Neuraville Inc.";
export const DEFAULT_OG_IMAGE_PATH = "/img/nrs-neurorobotics-studio-icon.png";
export const DEFAULT_OG_IMAGE_ALT = "Neurorobotics Studio";

const defaultSiteUrl =
  process.env.NEXT_PUBLIC_NODE_ENV === "staging"
    ? "https://staging.brainsforrobots.com"
    : "https://brainsforrobots.com";

/** Resolves canonical site origin from env (required in production builds). */
export function getSiteUrl(): string {
  const raw = process.env.NEXT_PUBLIC_URL || defaultSiteUrl;
  return raw.replace(/\/$/, "");
}

export function absoluteUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${getSiteUrl()}${normalized}`;
}

const defaultOpenGraphImages = [
  {
    url: DEFAULT_OG_IMAGE_PATH,
    width: 512,
    height: 512,
    alt: DEFAULT_OG_IMAGE_ALT,
  },
];

export const HOME_TITLE = "Neurorobotics Studio: Brain-Inspired AI for Robots";
export const HOME_H1 = "Neurorobotics Studio: brain-inspired AI for robots";
export const HOME_DESCRIPTION =
  "Design, train, and deploy brain-inspired AI on simulated or physical robots. Adapts on-device — no retraining cycles or GPU clusters. Powered by FEAGI, the open-source brain-inspired AI engine.";

export const PRICING_TITLE = "Plans & pricing | Neurorobotics Studio";
export const PRICING_DESCRIPTION =
  "Compare Neurorobotics Studio Community, Pro, and Team plans. Sign in to subscribe or manage your subscription.";

export const EMBODIMENTS_TITLE = "Embodiment Explorer | Neurorobotics Studio";
export const EMBODIMENTS_DESCRIPTION =
  "Browse robots, simulators, and hardware platforms compatible with FEAGI and Neurorobotics Studio.";

export const HUB_TITLE = "Brain Hub | Neurorobotics Studio";
export const HUB_DESCRIPTION =
  "Discover, share, and download public brain-inspired AI genomes for FEAGI and Neurorobotics Studio.";

export function buildOpenGraph(
  title: string,
  description: string,
  path: string,
  type: "website" | "article" = "website"
): Metadata["openGraph"] {
  return {
    title,
    description,
    type,
    url: path,
    siteName: SITE_NAME,
    images: defaultOpenGraphImages,
  };
}

export function buildTwitter(
  title: string,
  description: string
): Metadata["twitter"] {
  return {
    card: "summary_large_image",
    title,
    description,
    images: [DEFAULT_OG_IMAGE_PATH],
  };
}

export function buildHomeMetadata(): Metadata {
  return {
    title: HOME_TITLE,
    description: HOME_DESCRIPTION,
    alternates: { canonical: "/" },
    openGraph: buildOpenGraph(HOME_TITLE, HOME_DESCRIPTION, "/"),
    twitter: buildTwitter(HOME_TITLE, HOME_DESCRIPTION),
  };
}

export function buildPricingMetadata(): Metadata {
  return {
    title: PRICING_TITLE,
    description: PRICING_DESCRIPTION,
    alternates: { canonical: "/pricing" },
    openGraph: buildOpenGraph(PRICING_TITLE, PRICING_DESCRIPTION, "/pricing"),
    twitter: buildTwitter(PRICING_TITLE, PRICING_DESCRIPTION),
  };
}

export function buildEmbodimentsMetadata(): Metadata {
  return {
    title: EMBODIMENTS_TITLE,
    description: EMBODIMENTS_DESCRIPTION,
    alternates: { canonical: "/embodiments" },
    openGraph: buildOpenGraph(
      EMBODIMENTS_TITLE,
      EMBODIMENTS_DESCRIPTION,
      "/embodiments"
    ),
    twitter: buildTwitter(EMBODIMENTS_TITLE, EMBODIMENTS_DESCRIPTION),
  };
}

export function buildHubMetadata(): Metadata {
  return {
    title: HUB_TITLE,
    description: HUB_DESCRIPTION,
    alternates: { canonical: "/hub" },
    openGraph: buildOpenGraph(HUB_TITLE, HUB_DESCRIPTION, "/hub"),
    twitter: buildTwitter(HUB_TITLE, HUB_DESCRIPTION),
  };
}

export const FEAGI_TITLE = "FEAGI | Open-Source Neuromorphic AI Engine for Robots";
export const FEAGI_DESCRIPTION =
  "Free, open-source neuromorphic AI for robots. Spiking neural networks that learn on-device with no backprop or GPU cluster. Python, Rust, and Java SDKs.";

export const FEAGI_ARCHITECTURE_TITLE = "feagi-core Architecture | Brains for Robots";
export const FEAGI_ARCHITECTURE_DESCRIPTION =
  "Interactive crate dependency graph for feagi-core, the Rust workspace powering FEAGI's neural processing engine.";

export function buildFeagiMetadata(): Metadata {
  return {
    title: FEAGI_TITLE,
    description: FEAGI_DESCRIPTION,
    alternates: { canonical: "/feagi" },
    openGraph: {
      ...buildOpenGraph(FEAGI_TITLE, FEAGI_DESCRIPTION, "/feagi"),
      images: [{ url: "/img/feagi-logo.png", alt: "FEAGI" }],
    },
    twitter: buildTwitter(FEAGI_TITLE, FEAGI_DESCRIPTION),
  };
}

export function buildFeagiArchitectureMetadata(): Metadata {
  return {
    title: FEAGI_ARCHITECTURE_TITLE,
    description: FEAGI_ARCHITECTURE_DESCRIPTION,
    alternates: { canonical: "/feagi/architecture" },
    openGraph: {
      ...buildOpenGraph(
        FEAGI_ARCHITECTURE_TITLE,
        FEAGI_ARCHITECTURE_DESCRIPTION,
        "/feagi/architecture"
      ),
      images: [{ url: "/img/feagi-logo.png", alt: "FEAGI" }],
    },
    twitter: buildTwitter(FEAGI_ARCHITECTURE_TITLE, FEAGI_ARCHITECTURE_DESCRIPTION),
  };
}

export const FEAGI_RESEARCH_TITLE =
  "FEAGI Citations & Research Papers | Brains for Robots";
export const FEAGI_RESEARCH_DESCRIPTION =
  "Peer-reviewed IEEE and ACM publications describing FEAGI, the open-source neuromorphic AI engine. Full citations, DOIs, and BibTeX for academic use.";

export function buildFeagiResearchMetadata(): Metadata {
  return {
    title: FEAGI_RESEARCH_TITLE,
    description: FEAGI_RESEARCH_DESCRIPTION,
    alternates: { canonical: "/feagi/research" },
    openGraph: {
      ...buildOpenGraph(
        FEAGI_RESEARCH_TITLE,
        FEAGI_RESEARCH_DESCRIPTION,
        "/feagi/research"
      ),
      images: [{ url: "/img/feagi-logo.png", alt: "FEAGI" }],
    },
    twitter: buildTwitter(FEAGI_RESEARCH_TITLE, FEAGI_RESEARCH_DESCRIPTION),
  };
}

/** Shared metadata builder for static marketing pages (contact, book, etc.). */
export function buildMarketingPageMetadata(
  title: string,
  description: string,
  path: string
): Metadata {
  return {
    title,
    description,
    alternates: { canonical: path },
    openGraph: buildOpenGraph(title, description, path),
    twitter: buildTwitter(title, description),
  };
}

export function buildEmbodimentDetailMetadata(
  title: string,
  description: string,
  path: string,
  imageUrl?: string
): Metadata {
  const pageTitle = `${title} | Neurorobotics Studio`;
  const images = imageUrl
    ? [{ url: imageUrl, alt: title }]
    : defaultOpenGraphImages;

  return {
    title: pageTitle,
    description: description || EMBODIMENTS_DESCRIPTION,
    alternates: { canonical: path },
    openGraph: {
      ...buildOpenGraph(pageTitle, description || EMBODIMENTS_DESCRIPTION, path),
      images,
    },
    twitter: {
      ...buildTwitter(pageTitle, description || EMBODIMENTS_DESCRIPTION),
      images: imageUrl ? [imageUrl] : [DEFAULT_OG_IMAGE_PATH],
    },
  };
}

export const PARTNERS_MEDIA_KIT_TITLE = "Partner Media Kit | Neurorobotics Studio";
export const PARTNERS_MEDIA_KIT_DESCRIPTION =
  "Official Neurorobotics Studio media kit for partners. Download brand logos, app screenshots, demo videos, social banners, and sample copy. Everything you need to represent NRS in your content.";

export function buildPartnersMediaKitMetadata(): Metadata {
  return {
    title: PARTNERS_MEDIA_KIT_TITLE,
    description: PARTNERS_MEDIA_KIT_DESCRIPTION,
    alternates: { canonical: "/partners/media-kit" },
    openGraph: buildOpenGraph(
      PARTNERS_MEDIA_KIT_TITLE,
      PARTNERS_MEDIA_KIT_DESCRIPTION,
      "/partners/media-kit"
    ),
    twitter: buildTwitter(PARTNERS_MEDIA_KIT_TITLE, PARTNERS_MEDIA_KIT_DESCRIPTION),
  };
}

export const PARTNERS_TITLE = "Partner Program | Neurorobotics Studio";
export const PARTNERS_DESCRIPTION =
  "Earn commission by introducing Neurorobotics Studio to the world. Built for sales professionals, influencers, educators, and content creators. Share your unique link and get paid for every conversion.";

export function buildPartnersMetadata(): Metadata {
  return {
    title: PARTNERS_TITLE,
    description: PARTNERS_DESCRIPTION,
    alternates: { canonical: "/partners" },
    openGraph: buildOpenGraph(PARTNERS_TITLE, PARTNERS_DESCRIPTION, "/partners"),
    twitter: buildTwitter(PARTNERS_TITLE, PARTNERS_DESCRIPTION),
  };
}

type LegalSection = "privacy-policy" | "terms-of-service";

const legalMetadata: Record<
  LegalSection,
  { title: string; description: string; path: string }
> = {
  "privacy-policy": {
    title: "Privacy Policy | Neurorobotics Studio",
    description:
      "Read the Neuraville privacy policy for Brains for Robots and Neurorobotics Studio.",
    path: "/legal/privacy-policy",
  },
  "terms-of-service": {
    title: "Terms of Service | Neurorobotics Studio",
    description:
      "Read the Neuraville terms of service for Brains for Robots and Neurorobotics Studio.",
    path: "/legal/terms-of-service",
  },
};

export function buildLegalPageMetadata(section: LegalSection): Metadata {
  const entry = legalMetadata[section];
  return {
    title: entry.title,
    description: entry.description,
    alternates: { canonical: entry.path },
    robots: {
      index: false,
      follow: false,
    },
    openGraph: buildOpenGraph(entry.title, entry.description, entry.path),
    twitter: buildTwitter(entry.title, entry.description),
  };
}
